import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

/**
 * QIRichTextEditor
 *
 * A shareable rich text editor built on Tiptap (ProseMirror). It renders a
 * bordered field with a label on the left (matching the FMDP form-field look)
 * and a toolbar + editable surface on the right.
 *
 * Value contract:
 * - `value` is an HTML string. `onChange(html)` fires with the editor's HTML
 *   on every document change. When the editor is empty, `onChange` emits "".
 *
 * Toolbar controls:
 *   Heading, Paragraph, Bullet list, Bold, Italic, and Line break.
 *
 * @param {string} label field label shown on the left
 * @param {string} value current HTML value
 * @param {func} onChange called with the new HTML string on every change
 * @param {boolean} readOnly when true the editor is not editable and the
 *   toolbar is hidden
 * @param {string} placeholder placeholder shown when empty
 * @param {string} error error message rendered below the field
 * @param {string} className extra class names for the outer wrapper
 */
export const QIRichTextEditor = ({
  label,
  value = "",
  onChange = () => {},
  readOnly = false,
  placeholder = "",
  error = "",
  className = "",
}) => {
    // Guard so we don't echo a programmatic setContent back through onChange.
    const isSettingContent = useRef(false);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
        }),
        Placeholder.configure({ placeholder }),
      ],
      content: value || "",
      editable: !readOnly,
      onUpdate: ({ editor: instance }) => {
        if (isSettingContent.current) return;
        const html = instance.getHTML();
        // Tiptap represents an empty doc as "<p></p>"; normalize to "".
        onChange(instance.isEmpty ? "" : html);
      },
      editorProps: {
        attributes: {
          class: "qi-rte_content",
        },
      },
    });

    // Keep the editor in sync when the value prop changes externally (e.g. a
    // form reset / cancel) without clobbering the caret during normal typing.
    useEffect(() => {
      if (!editor) return;
      const current = editor.isEmpty ? "" : editor.getHTML();
      if (value !== current) {
        isSettingContent.current = true;
        editor.commands.setContent(value || "", false);
        isSettingContent.current = false;
      }
    }, [value, editor]);

    // Reflect readOnly changes onto the live editor instance.
    useEffect(() => {
      if (editor) editor.setEditable(!readOnly);
    }, [readOnly, editor]);

    const isActive = (name, attrs) =>
      editor && editor.isActive(name, attrs) ? "is-active" : "";

    return (
      <div
        className={`qi-rte qi-form-element ${error ? "error" : ""} ${
          readOnly ? "disabled" : ""
        } ${className}`}
      >
        {label && <label className="qi-rte_label">{label}</label>}

        <div className="qi-rte_wrapper">
          {!readOnly && editor && (
            <div className="qi-rte_toolbar" role="toolbar">
              <button
                type="button"
                title="Heading"
                className={`qi-rte_btn ${isActive("heading", { level: 2 })}`}
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                }
              >
                H
              </button>
              <button
                type="button"
                title="Paragraph"
                className={`qi-rte_btn ${isActive("paragraph")}`}
                onClick={() => editor.chain().focus().setParagraph().run()}
              >
                &para;
              </button>
              <button
                type="button"
                title="Bullet list"
                className={`qi-rte_btn ${isActive("bulletList")}`}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                &#9776;
              </button>
              <span className="qi-rte_divider" />
              <button
                type="button"
                title="Bold"
                className={`qi-rte_btn qi-rte_btn--bold ${isActive("bold")}`}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                B
              </button>
              <button
                type="button"
                title="Italic"
                className={`qi-rte_btn qi-rte_btn--italic ${isActive("italic")}`}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                I
              </button>
              <button
                type="button"
                title="Line break"
                className="qi-rte_btn"
                onClick={() => editor.chain().focus().setHardBreak().run()}
              >
                &#8629;
              </button>
            </div>
          )}

          <EditorContent editor={editor} className="qi-rte_editor" />
        </div>

        {error && <p className="qi-rte_error">{error}</p>}
      </div>
    );
  };

QIRichTextEditor.displayName = "QIRichTextEditor";

QIRichTextEditor.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  className: PropTypes.string,
};
